/* eslint-disable max-statements */
import { FC, useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { OfferCheckout as OfferCheckoutComponent } from 'components/offerCheckout/offerCheckout'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Loader } from '../../components/loader'
import { RouterPathes } from '../../constants/router-paths'
import { url } from '../../constants/urls'
import { getOffers } from '../../services/offers/offers'
import { getUserDetails } from '../../services/userDetails/userDetails'
import { useGuildOffers } from '../../services/guilds/GET'

export const OfferCheckout: FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const {
    data: guildOffers,
    isLoading: isGuildOffersLoading,
    refetch: refetchGuildOffers
  } = useGuildOffers()
  const {
    data: offers,
    isLoading: isOffersLoading,
    refetch: refetchOffers
  } = useQuery({
    queryKey: ['offers'],
    queryFn: getOffers,
    refetchOnWindowFocus: false
  })
  const { data: userDetails, isLoading: isUserDetailsLoading } = useQuery({
    queryKey: ['userDetails'],
    queryFn: getUserDetails,
    refetchOnWindowFocus: false,
    throwOnError: error => {
      if (error instanceof AxiosError && error.status === 401) {
        const returnLoginUrl = window.location.href

        window.location.href = url.redirectLogin(returnLoginUrl)
      }

      return true
    },
    retry: 0
  })
  const currentOfferId = searchParams.get('offerId')
  const currentOfferType = searchParams.get('type')
  const offer = useMemo(() => {
    if (!currentOfferId) return undefined

    if (currentOfferType === 'regular' && offers) {
      return offers.offers.find(({ id }) => id === currentOfferId)
    }

    if (currentOfferType === 'guild' && guildOffers) {
      return guildOffers.offers.find(({ id }) => id === currentOfferId)
    }

    return undefined
  }, [offers, guildOffers, currentOfferId, currentOfferType])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [currentOfferId, currentOfferType])

  const guildMembers = useMemo(() => {
    if (!guildOffers) return []

    return guildOffers.members.map(member => member)
  }, [guildOffers])

  if (isOffersLoading || isUserDetailsLoading || isGuildOffersLoading) {
    return (
      <div className='flex flex-1 items-center justify-center'>
        <Loader
          width={80}
          height={80}
        />
      </div>
    )
  }

  if (!currentOfferId || !offer || !userDetails) {
    navigate(RouterPathes.home)

    // eslint-disable-next-line consistent-return
    return
  }

  return (
    <OfferCheckoutComponent
      offer={offer}
      userDetails={userDetails}
      currentOfferType={currentOfferType as 'regular' | 'guild'}
      guildMembers={guildMembers}
      refetchGuildOffers={refetchGuildOffers}
      refetchOffers={refetchOffers}
    />
  )
}
